import React, { useState, useEffect, useContext } from 'react';
import { StateContext } from '../../../../StateProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUserData, fetchStaffBenefits, saveStaffBenefitAdjustment } from '../../../../Utilities/api';
import { BackButton } from '../../../../Utilities/components/Buttons';
import './UserInfo.css';

/** ES6-safe value fallback */
function uiVal(v, fallback) {
  if (v === null || v === undefined || v === '') {
    return (typeof fallback !== 'undefined' ? fallback : 'N/A');
  }
  return v;
}

/** ES6-safe date prettifier */
function uiPrettyDate(iso) {
  if (!iso) return 'N/A';
  try { return new Date(iso).toLocaleDateString(); } catch (e) { return 'Invalid Date'; }
}

/** ES6-safe phone formatter */
function uiPhone(v) {
  if (!v) return 'N/A';
  var cleaned = String(v).replace(/\D/g, '');
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? (match[1] + '-' + match[2] + '-' + match[3]) : v;
}

const UserInfo = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const context = useContext(StateContext) || {};
  const currentRole = String((context.user && context.user.role) || context.role || '').toLowerCase();
  const canManageBenefits = currentRole === 'admin' || currentRole === 'manager';

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [benefitsLoading, setBenefitsLoading] = useState(true);
  const [benefitsErrors, setBenefitsErrors] = useState([]);
  const [benefitAction, setBenefitAction] = useState('');
  const [form, setForm] = useState({ planType: 'Club', billingStatus: 'active', bundleType: '10-Pack', familyMemberId: '', remainingDays: '', amountCollected: '0', paymentMethod: 'cash', paymentReference: '', reason: '' });
  const [benefitMessage, setBenefitMessage] = useState('');
  const [savingBenefit, setSavingBenefit] = useState(false);

  useEffect(function onMount() {
    async function run() {
      try {
        const data = await fetchUserData(userId);
        setUser(data);
      } catch (err) {
        setError((err && err.message) ? err.message : 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    }
    run();
  }, [userId]);

  function loadBenefits() {
    setBenefitsLoading(true);
    setBenefitsErrors([]);
    return fetchStaffBenefits(userId).then(function (data) {
      setMemberships(Array.isArray(data.memberships) ? data.memberships : []);
      setBundles(Array.isArray(data.bundles) ? data.bundles : []);
    }).catch(function (err) {
      setBenefitsErrors([(err.response && err.response.data && (err.response.data.message || err.response.data.error)) || 'Unable to load account benefits']);
    }).finally(function () { setBenefitsLoading(false); });
  }

  useEffect(function () {
    let active = true;
    fetchStaffBenefits(userId).then(function (data) {
      if (!active) return;
      setMemberships(Array.isArray(data.memberships) ? data.memberships : []);
      setBundles(Array.isArray(data.bundles) ? data.bundles : []);
    }).catch(function (err) {
      if (active) setBenefitsErrors([(err.response && err.response.data && err.response.data.message) || 'Unable to load account benefits']);
    }).finally(function () { if (active) setBenefitsLoading(false); });
    return function () { active = false; };
  }, [userId]);

  function setField(name, value) {
    setForm(function (old) { return Object.assign({}, old, { [name]: value }); });
  }

  function beginBenefitAction(action, target) {
    setBenefitMessage('');
    setBenefitAction(action);
    setForm({
      planType: target && target.planType || 'Club', billingStatus: target && target.billingStatus || 'active',
      bundleType: '10-Pack', membershipId: target && target.id || '',
      bundleId: target && target.id || '', familyMemberId: '',
      remainingDays: target && target.remainingDays != null ? String(target.remainingDays) : '',
      amountCollected: '0', paymentMethod: 'cash', paymentReference: '',
      reason: ''
    });
  }

  async function saveBenefit() {
    if (!canManageBenefits) return;
    setBenefitMessage('');
    if (!form.reason || form.reason.trim().length < 5) {
      setBenefitMessage('Enter a reason for the manual change (at least 5 characters).');
      return;
    }
    const amount = Number(form.amountCollected || 0);
    if (amount < 0 || !Number.isFinite(amount)) {
      setBenefitMessage('Enter a valid amount collected.');
      return;
    }
    setSavingBenefit(true);
    try {
      await saveStaffBenefitAdjustment(userId, Object.assign({}, form, {
        action: benefitAction, amountCollected: amount,
        familyMemberId: form.familyMemberId || null
      }));
      setBenefitAction('');
      setBenefitMessage('Manual change saved. No automatic Stripe charge was made.');
      await loadBenefits();
    } catch (err) {
      setBenefitMessage((err.response && err.response.data && err.response.data.message) || err.message || 'Unable to save account benefit');
    } finally {
      setSavingBenefit(false);
    }
  }

  if (loading) {
    return (
      <div className="ui-userinfo-loading">
        <p className="ui-userinfo-loading-text">Loading user information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ui-userinfo-error">
        Error: {error}
        <button
          className="ui-userinfo-error-btn"
          onClick={function(){ navigate('/console/users'); }}
        >
          Back to List
        </button>
      </div>
    );
  }

  if (!user) return <div className="ui-userinfo-missing">User not found.</div>;

  function handlePetClick(member) {
    navigate('/console/petInfo/' + member.id);
    try { window.scrollTo(0, 0); } catch (e) {}
  }

  return (
    <div className="ui-userinfo-page">
      {/* Top bar */}
      <div className="ui-userinfo-topbar">
        <BackButton />
        <div className="ui-userinfo-actions">
          <button
            className="ui-btn ui-btn-pink"
            onClick={function(){ navigate('/console/userBookingInfo/' + userId); }}
          >
            Bookings
          </button>
          <button
            className="ui-btn ui-btn-purple"
            onClick={function(){ navigate('/console/userPaymentHistory/' + userId); }}
          >
            Transactions
          </button>
        </div>
      </div>

      {/* Profile Summary */}
      <section className="ui-card ui-userinfo-profile">
        <div className="ui-userinfo-avatar">
          {user.image
            ? <img src={user.image} alt={'Avatar of ' + uiVal(user.username, 'User')} />
            : <div className="ui-userinfo-avatar-fb">{(uiVal(user.username, 'U') + '').charAt(0)}</div>
          }
        </div>

        <div className="ui-userinfo-summary">
          <h1 className="ui-userinfo-title">{uiVal(user.username, 'User')}</h1>
          <div className="ui-userinfo-summary-grid">
            <div className="ui-kv">
              <span className="ui-kv-label">Email</span>
              <span className="ui-kv-value">{uiVal(user.email)}</span>
            </div>
            <div className="ui-kv">
              <span className="ui-kv-label">Phone</span>
              <span className="ui-kv-value">{uiPhone(user.phone)}</span>
            </div>
            <div className="ui-kv">
              <span className="ui-kv-label">Store</span>
              <span className="ui-kv-value">{uiVal(user.storeLocation)}</span>
            </div>
            <div className="ui-kv">
              <span className="ui-kv-label">Referral</span>
              <span className="ui-kv-value">{uiVal(user.referralCode)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div className="ui-userinfo-sections">

        <section className="ui-card">
          <h2 className="ui-section-title">Membership &amp; Benefits</h2>
          {benefitsLoading ? <p className="ui-benefits-muted">Loading current benefits…</p> : (
            <>
              {benefitsErrors.map(function (message) { return <p className="ui-benefits-error" key={message}>{message}</p>; })}
              {(() => {
                const activeMemberships = memberships.filter(function (m) { return m.billingStatus === 'active' && !m.cancelledAt; });
                const priorMemberships = memberships.filter(function (m) { return m.billingStatus !== 'active' || m.cancelledAt; });
                const activeBundles = bundles.filter(function (b) { return b.status === 'active' && Number(b.remainingDays) > 0 && (!b.expiryDate || b.expiryDate >= new Date().toISOString().slice(0, 10)); });
                const priorBundles = bundles.filter(function (b) { return activeBundles.every(function (a) { return a.id !== b.id; }); });
                const membershipCard = function (m, historical) {
                  return <div className="ui-benefits-entry" key={m.id}>
                    <div className="ui-benefits-heading"><strong>{uiVal(m.planType)} Membership</strong><span className="ui-benefits-tag">{historical ? 'Previous membership' : 'Active'}</span></div>
                    <div className="ui-benefits-line"><span>Member discount</span><strong>{Number(m.discountPercent || 0)}%</strong></div>
                    <div className="ui-benefits-line"><span>Started</span><strong>{uiPrettyDate(m.startDate)}</strong></div>
                    {!historical && <div className="ui-benefits-line"><span>Next billing</span><strong>{m.nextBillingDate ? uiPrettyDate(m.nextBillingDate) : 'Managed manually'}</strong></div>}
                    {historical && <div className="ui-benefits-line"><span>Period ended</span><strong>{uiPrettyDate(m.cancelledAt || m.endDate || m.nextBillingDate)}</strong></div>}
                    {!historical && canManageBenefits && <button className="ui-benefit-action" type="button" onClick={function () { beginBenefitAction('membership_update', m); }}>Edit membership</button>}
                  </div>;
                };
                const bundleCard = function (b, historical) {
                  const pet = (user.FamilyMembers || []).find(function (p) { return String(p.id) === String(b.familyMemberId); });
                  return <div className="ui-benefits-entry" key={b.id}>
                    <div className="ui-benefits-heading"><strong>{b.bundleType}</strong><span className="ui-benefits-tag">{historical ? 'Previous bundle' : 'Active'}</span></div>
                    <div className="ui-benefits-balance">{Number(b.remainingDays || 0)} <small>/ {b.totalDays} days remaining</small></div>
                    <div className="ui-benefits-meter"><span style={{width:(b.totalDays > 0 ? Math.min(100, b.remainingDays / b.totalDays * 100) : 0) + '%'}} /></div>
                    <div className="ui-benefits-line"><span>Assigned pet</span><strong>{pet ? pet.name : b.familyMemberId ? 'Pet #' + b.familyMemberId : 'Account bundle'}</strong></div>
                    <div className="ui-benefits-line"><span>Expires</span><strong>{uiPrettyDate(b.expiryDate)}</strong></div>
                    {!historical && <button className="ui-benefit-action" type="button" onClick={function () { beginBenefitAction('bundle_adjust', b); }}>Adjust remaining days</button>}
                  </div>;
                };
                return <div className="ui-benefits-grid">
                  <div className="ui-benefits-panel">
                    <h3>Club Membership</h3>
                    {activeMemberships.length ? activeMemberships.map(function (m) { return membershipCard(m, false); }) : <p className="ui-benefits-muted">No current active membership</p>}
                    {canManageBenefits && <button className="ui-benefit-action" type="button" onClick={function () { beginBenefitAction('membership_create'); }}>+ Add manual membership</button>}
                    {priorMemberships.length > 0 && <details className="ui-benefits-history"><summary>Previous memberships ({priorMemberships.length})</summary>{priorMemberships.map(function (m) { return membershipCard(m, true); })}</details>}
                  </div>
                  <div className="ui-benefits-panel">
                    <h3>Daycare Bundles</h3>
                    {activeBundles.length ? activeBundles.map(function (b) { return bundleCard(b, false); }) : <p className="ui-benefits-muted">No current active bundle</p>}
                    {canManageBenefits && <button className="ui-benefit-action" type="button" onClick={function () { beginBenefitAction('bundle_create'); }}>+ Add manual bundle purchase</button>}
                    {priorBundles.length > 0 && <details className="ui-benefits-history"><summary>Previous bundles ({priorBundles.length})</summary>{priorBundles.map(function (b) { return bundleCard(b, true); })}</details>}
                  </div>
                </div>;
              })()}
            </>
          )}
          {canManageBenefits && benefitAction && <div className="ui-benefits-editor">
            <h3>{benefitAction === 'membership_create' ? 'Add membership' : benefitAction === 'membership_update' ? 'Update membership status' : benefitAction === 'bundle_create' ? 'Add bundle purchase' : 'Adjust bundle balance'}</h3>
            {benefitAction === 'membership_create' && <label>Plan<select value={form.planType} onChange={function (e) { setField('planType', e.target.value); }}><option value="Club">Club (10%)</option><option value="Plus">Plus (20%)</option></select></label>}
            {benefitAction === 'membership_update' && <label>Plan<select value={form.planType} onChange={function (e) { setField('planType', e.target.value); }}><option value="Club">Club (10%)</option><option value="Plus">Plus (20%)</option></select></label>}
            {benefitAction === 'membership_update' && <label>Status<select value={form.billingStatus} onChange={function (e) { setField('billingStatus', e.target.value); }}><option value="active">Active</option><option value="paused">Paused</option><option value="canceled">End membership</option></select></label>}
            {benefitAction === 'bundle_create' && <>
              <label>Bundle<select value={form.bundleType} onChange={function (e) { setField('bundleType', e.target.value); }}><option value="10-Pack">10-Pack</option><option value="20-Pack">20-Pack</option></select></label>
              <label>Pet<select value={form.familyMemberId} onChange={function (e) { setField('familyMemberId', e.target.value); }}><option value="">Account bundle (all pets)</option>{(user.FamilyMembers || []).filter(function (p) { return p.type !== 'Human'; }).map(function (p) { return <option key={p.id} value={p.id}>{p.name}</option>; })}</select></label>
            </>}
            {benefitAction === 'bundle_adjust' && <label>Days remaining<input type="number" min="0" step="1" value={form.remainingDays} onChange={function (e) { setField('remainingDays', e.target.value); }} /></label>}
            <label>Amount already collected outside Pawsible ($)<input type="number" min="0" step="0.01" value={form.amountCollected} onChange={function (e) { setField('amountCollected', e.target.value); }} /></label>
            {Number(form.amountCollected) > 0 && <>
              <label>External payment method<select value={form.paymentMethod} onChange={function (e) { setField('paymentMethod', e.target.value); }}><option value="cash">Cash</option><option value="card_external">External card</option><option value="bank_transfer">Bank transfer</option><option value="other">Other</option></select></label>
              <label>Receipt/reference<input value={form.paymentReference} onChange={function (e) { setField('paymentReference', e.target.value); }} /></label>
            </>}
            <label>Reason for manual change<textarea value={form.reason} onChange={function (e) { setField('reason', e.target.value); }} placeholder="Required for audit history" rows={2} /></label>
            <p className="ui-benefits-muted">This records a manual adjustment and any externally received payment. It does not charge Stripe or initiate recurring billing.</p>
            <div className="ui-benefits-controls"><button type="button" className="ui-btn ui-btn-primary" disabled={savingBenefit} onClick={saveBenefit}>{savingBenefit ? 'Saving…' : 'Save manual change'}</button><button type="button" className="ui-btn" onClick={function () { setBenefitAction(''); }}>Cancel</button></div>
          </div>}
          {benefitMessage && <p className="ui-benefits-message" role="status">{benefitMessage}</p>}
        </section>

        {/* Family Members */}
        {user.FamilyMembers && user.FamilyMembers.length > 0 && (
          <section className="ui-card">
            <h2 className="ui-section-title">Family Members</h2>
            <div className="ui-family-grid">
              {user.FamilyMembers.map(function(member){
                return (
                  <div
                    key={member.id}
                    className="ui-family-card"
                    onClick={function(){ handlePetClick(member); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={function(e){ if (e.key === 'Enter') handlePetClick(member); }}
                  >
                    <div className="ui-family-head">
                      <div className="ui-pet-thumb">
                        {member.image
                          ? <img src={member.image} alt={uiVal(member.name, 'Pet')} />
                          : <div className="ui-pet-thumb-fb">{(uiVal(member.name, 'P') + '').charAt(0)}</div>
                        }
                      </div>
                      <div className="ui-pet-id">
                        <div className="ui-pet-name">{uiVal(member.name)}</div>
                        <div className="ui-badge ui-badge-secondary">{uiVal(member.type)}</div>
                      </div>
                    </div>

                    <div className="ui-family-details-grid">
                      {member.type !== 'Human' ? (
                        <React.Fragment>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Breed</span>
                            <span className="ui-f-value">{uiVal(member.breed)}</span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Age</span>
                            <span className="ui-f-value">{uiVal(member.age)}</span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">DOB</span>
                            <span className="ui-f-value">{uiPrettyDate(member.dob)}</span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Vax</span>
                            <span className="ui-f-value">
                              {member.vaccination
                                ? (<a className="ui-link" href={member.vaccination} target="_blank" rel="noopener noreferrer">View</a>)
                                : 'N/A'
                              }
                            </span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Daycare</span>
                            <span className={member.daycare ? 'ui-tag ui-tag-ok' : 'ui-tag ui-tag-no'}>
                              {member.daycare ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </React.Fragment>
                      ) : (
                        <div className="ui-f-field">
                          <span className="ui-f-label">Relationship</span>
                          <span className="ui-f-value">{uiVal(member.relationship)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Wallet */}
        {user.Wallet && (
          <section className="ui-card">
            <h2 className="ui-section-title">Wallet</h2>
            <div className="ui-kv-grid">
              <div className="ui-kv"><span className="ui-kv-label">Balance</span><span className="ui-kv-value">${uiVal(user.Wallet.balance, '0.00')}</span></div>
              <div className="ui-kv"><span className="ui-kv-label">Total Spent</span><span className="ui-kv-value">${uiVal(user.Wallet.totalSpent, '0.00')}</span></div>
            </div>
          </section>
        )}

        {/* Payment Methods */}
        {user.PaymentMethods && user.PaymentMethods.length > 0 && (
          <section className="ui-card">
            <h2 className="ui-section-title">Payment Methods</h2>
            <div className="ui-pm-grid">
              {user.PaymentMethods.map(function(pm){
                return (
                  <div className="ui-pm-card" key={pm.id}>
                    <div className="ui-pm-line"><span className="ui-kv-label">Type</span><span className="ui-kv-value">{uiVal(pm.cardType)}</span></div>
                    <div className="ui-pm-line"><span className="ui-kv-label">Number</span><span className="ui-kv-value">{uiVal(pm.cardNumber)}</span></div>
                    <div className="ui-pm-line"><span className="ui-kv-label">Name</span><span className="ui-kv-value">{uiVal(pm.cardholderName)}</span></div>
                    <div className="ui-pm-line"><span className="ui-kv-label">Expiry</span><span className="ui-kv-value">{uiVal(pm.expiryMonth)}/{uiVal(pm.expiryYear)}</span></div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default UserInfo;
